import { useEffect, useMemo } from 'react';
import find from 'lodash/find';
import first from 'lodash/first';

import {
  CategoryID,
  FetchCategoriesScopes,
  FetchCategoriesSortTypes
} from '../../../categories/categoriesTypes';

import { FETCH_CACHE_PRODUCT_CATEGORIES_QUERY } from '../../../products/queries/fetchCacheProductCategories.query';
import {
  FETCH_PRODUCT_CATEGORIES_QUERY,
  FetchProductCategoriesQueryResponse
} from '../../../products/queries/fetchProductCategories.query';

import { useFinPaginatedCategories } from '../../../categories/hooks/useFinPaginatedCategories';
import { useShowToastOnErrorChange } from '../../../../common/hooks/useShowToastOnErrorChange';

import { StockTabs } from '../../stockConstants';
import { CategoriesPermissions } from '../../../categories/categoriesConstants';

import { MaterialCache } from '../../../materials/MaterialCache';
import { LifestyleCache } from '../../../lifestyles/LifestyleCache';
import { ProductCache } from '../../../products/ProductCache';

const categoryScopes = {
  [StockTabs.PRODUCTS]: FetchCategoriesScopes.PRODUCT_GROUPS,
  [StockTabs.LIFESTYLES]: FetchCategoriesScopes.LIFESTYLE_GROUPS,
  [StockTabs.MATERIALS]: FetchCategoriesScopes.MATERIAL_CATEGORIES
};

const categoryCacheKeys = {
  [StockTabs.PRODUCTS]: ProductCache.categoriesCacheKey(),
  [StockTabs.LIFESTYLES]: LifestyleCache.categoriesCacheKey(),
  [StockTabs.MATERIALS]: MaterialCache.categoriesCacheKey()
};

const initialSort = [FetchCategoriesSortTypes.NAME_ASC];
const options = {
  staleTime: 1000 * 60 * 60,
  withoutPrefetch: true
};

interface ThreeDStockMaterialsCategoriesOptions {
  tab: StockTabs;
  categoryIds?: CategoryID[];
}

function useThreeDStockCategories({
  categoryIds,
  tab
}: ThreeDStockMaterialsCategoriesOptions) {
  const {
    categories,
    categoriesError,
    categoriesFetched,
    changeCategoriesFilters,
    categoriesFilters
  } = useFinPaginatedCategories<FetchProductCategoriesQueryResponse>({
    action: CategoriesPermissions.READ_PRODUCT_CATEGORIES_INDEX_ES_QUERY,
    cacheKey: categoryCacheKeys[tab],
    query: FETCH_PRODUCT_CATEGORIES_QUERY,
    cacheQuery: FETCH_CACHE_PRODUCT_CATEGORIES_QUERY,
    initialFilters: {
      scope: { eq: categoryScopes[tab] }
    },
    initialSort,
    initialLimit: 1000,
    options,
    trackTotalHits: true
  });

  useShowToastOnErrorChange({ error: categoriesError });

  useEffect(() => {
    if (categoriesFilters.scope.eq !== categoryScopes[tab]) {
      changeCategoriesFilters({
        scope: { eq: categoryScopes[tab] }
      });
    }
  }, [categoriesFilters.scope.eq, changeCategoriesFilters, tab]);

  const currentCategoryId = first(categoryIds);

  const currentCategory = useMemo(() => {
    return find(categories, ['id', first(categoryIds)]) || null;
  }, [categories, categoryIds]);

  return {
    currentCategory,
    currentCategoryId,
    categories,
    categoriesError,
    categoriesFetched,
    changeCategoriesFilters,
    categoriesFilters
  };
}

export default useThreeDStockCategories;
